import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import SEOHead from '../components/SEOHead';

const ChangePassword = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    number: false,
    special: false,
    upper: false,
    lower: false
  });

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Wait for auth to initialize before doing redirects
    if (authLoading) {
      return;
    }
    
    // Redirect if not authenticated
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    // Redirect if user is not verified
    if (user && !user.is_verified) {
      navigate(`/email-verification?email=${encodeURIComponent(user.email)}`);
      return;
    }
  }, [isAuthenticated, user, navigate, authLoading]);

  // Password validation for customer accounts
  useEffect(() => {
    const password = formData.newPassword;
    setPasswordStrength({
      length: password.length >= 8,
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      upper: /[A-Z]/.test(password),
      lower: /[a-z]/.test(password)
    });
  }, [formData.newPassword]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const isCustomer = user?.role === 'customer';
  const isPasswordValid = isCustomer ? Object.values(passwordStrength).every(Boolean) : formData.newPassword.length > 0;
  
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordValid) {
      setError(isCustomer ? 'Please meet all password requirements' : 'Please enter a new password');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await authAPI.changePassword(formData.currentPassword, formData.newPassword);
      setSuccess(true);
      
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
      
    } catch (error) {
      setError(error.response?.data?.detail || 'Password change failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
        <SEOHead 
          title="Password Changed - BrixBot"
          description="Password changed successfully"
          isIndexable={false}
        />
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8 text-center">
            <div className="mx-auto h-24 w-24 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <span className="text-4xl">✅</span>
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Password Changed Successfully!
            </h2>
            
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-6">
              <div className="flex items-center justify-center">
                <span className="text-green-500 mr-2 text-xl">🎉</span>
                Your password has been updated successfully!
              </div>
            </div>
            
            <p className="text-gray-600 mb-6">
              You will be redirected to your dashboard shortly.
            </p>
            
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-purple-700 transition duration-200"
            >
              Continue to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-purple-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <SEOHead 
        title="Change Password - BrixBot"
        description="Change your account password"
        isIndexable={false}
      />
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-24 w-24 bg-white rounded-full flex items-center justify-center mb-6">
            <span className="text-4xl">🔑</span>
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">
            Change Password
          </h2>
          <p className="text-purple-200">
            Update your password to keep your account secure
          </p>
        </div>

        {/* Change Password Form */}
        <div className="bg-white rounded-xl shadow-xl p-8">
          {/* User Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-sm">
                    {user?.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                <p className="text-sm text-gray-500 capitalize">
                  {user?.role} Account
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Current Password */}
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Current Password
              </label>
              <input
                id="currentPassword"
                name="currentPassword"
                type="password"
                value={formData.currentPassword}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 bg-white"
                placeholder="Enter current password"
                required
              />
            </div>

            {/* New Password */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                New Password
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                value={formData.newPassword}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 bg-white"
                placeholder="Enter new password"
                required
              />
              
              {/* Password Requirements for Customer Accounts */}
              {isCustomer && formData.newPassword && (
                <div className="mt-2 text-xs space-y-1">
                  <p className="text-gray-600 font-medium mb-2">Password must contain:</p>
                  <div className={`flex items-center ${passwordStrength.length ? 'text-green-600' : 'text-red-600'}`}>
                    <span className="mr-2">{passwordStrength.length ? '✓' : '✗'}</span>
                    At least 8 characters
                  </div>
                  <div className={`flex items-center ${passwordStrength.number ? 'text-green-600' : 'text-red-600'}`}>
                    <span className="mr-2">{passwordStrength.number ? '✓' : '✗'}</span>
                    Contains a number
                  </div>
                  <div className={`flex items-center ${passwordStrength.special ? 'text-green-600' : 'text-red-600'}`}>
                    <span className="mr-2">{passwordStrength.special ? '✓' : '✗'}</span>
                    Contains a special character
                  </div>
                  <div className={`flex items-center ${passwordStrength.upper ? 'text-green-600' : 'text-red-600'}`}>
                    <span className="mr-2">{passwordStrength.upper ? '✓' : '✗'}</span>
                    Contains an uppercase letter
                  </div>
                  <div className={`flex items-center ${passwordStrength.lower ? 'text-green-600' : 'text-red-600'}`}>
                    <span className="mr-2">{passwordStrength.lower ? '✓' : '✗'}</span>
                    Contains a lowercase letter
                  </div>
                </div>
              )}
            </div>

            {/* Confirm New Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 bg-white"
                placeholder="Confirm new password"
                required
              />
              
              {/* Password Match Indicator */}
              {formData.confirmPassword && (
                <div className={`mt-2 text-xs ${formData.newPassword === formData.confirmPassword ? 'text-green-600' : 'text-red-600'}`}>
                  <span className="mr-2">{formData.newPassword === formData.confirmPassword ? '✓' : '✗'}</span>
                  Passwords {formData.newPassword === formData.confirmPassword ? 'match' : 'do not match'}
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
                <div className="flex items-center">
                  <span className="text-red-500 mr-2">⚠️</span>
                  {error}
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !isPasswordValid || formData.newPassword !== formData.confirmPassword || !formData.currentPassword}
              className="w-full bg-purple-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Changing Password...
                </div>
              ) : (
                'Change Password'
              )}
            </button>
          </form>

          {/* Cancel Button */}
          <div className="mt-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-gray-100 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-200 transition duration-200"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Back to Dashboard Link */}
        <div className="text-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-purple-200 hover:text-white transition duration-200"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;